import React, { useState, useRef, useCallback } from 'react';

// A simple SVG icon for visual flair. You can replace this with an icon library if you use one.
const UploadIcon = () => (
  <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#888', marginBottom: '15px' }}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

function ResumeUploader({ onUploadComplete }) {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState({ message: '', type: 'idle' }); // 'idle', 'uploading', 'success', 'error'
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // --- Core API Call ---
  const handleUpload = useCallback(async () => {
    if (!file) {
      setStatus({ message: 'Please select a file first.', type: 'error' });
      return;
    }

    setStatus({ message: 'Analyzing resume...', type: 'uploading' });

    const formData = new FormData();
    formData.append('resume', file);

    try {
      // For best practice, store this URL in a .env file
      const apiUrl = process.env.REACT_APP_API_URL || 'https://coursehub-282e6.uc.r.appspot.com/api/upload-resume';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus({ message: result.message || 'Analysis complete!', type: 'success' });
        // ✅ CRITICAL FIX: Pass the entire 'analysis' object to the parent.
        if (onUploadComplete) {
          onUploadComplete(result.analysis);
        }
      } else {
        throw new Error(result.message || 'An unknown error occurred.');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setStatus({ message: err.message || 'Upload failed. Please try again.', type: 'error' });
    }
  }, [file, onUploadComplete]);
  
  // --- Drag and Drop Handlers ---
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setStatus({ message: droppedFile.name, type: 'idle' });
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setStatus({ message: selectedFile.name, type: 'idle' });
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };
  
  // --- Dynamic Styles ---
  const statusColors = {
    success: '#28a745',
    error: '#dc3545',
    uploading: '#007bff',
    idle: '#6c757d',
  };

  const dropzoneStyle = {
    border: `2px dashed ${isDragging ? '#007bff' : '#ccc'}`,
    borderRadius: '10px',
    padding: '30px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.3s ease',
    backgroundColor: isDragging ? '#f0f8ff' : '#f9f9f9',
  };

  const buttonStyle = {
    padding: '12px 25px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#007bff',
    color: 'white',
    cursor: status.type === 'uploading' ? 'not-allowed' : 'pointer',
    opacity: status.type === 'uploading' ? 0.6 : 1,
    marginTop: '20px',
  };

  return (
    <div style={{ maxWidth: '600px', margin: '20px auto', fontFamily: 'sans-serif' }}>
      <div 
        style={dropzoneStyle} 
        onDragOver={handleDragOver} 
        onDragLeave={handleDragLeave} 
        onDrop={handleDrop}
        onClick={openFilePicker}
      >
        <UploadIcon />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <p style={{ margin: 0 }}>
          {file ? file.name : 'Drag & drop your resume here, or click to select a file'}
        </p>
      </div>

      <div style={{ textAlign: 'center' }}>
        <button onClick={handleUpload} disabled={status.type === 'uploading'} style={buttonStyle}>
          {status.type === 'uploading' ? 'Analyzing...' : 'Analyze Resume'}
        </button>
      </div>

      {status.message && (
        <p style={{ marginTop: '15px', textAlign: 'center', color: statusColors[status.type], fontWeight: 'bold' }}>
          {status.message}
        </p>
      )}
    </div>
  );
}

export default ResumeUploader;